import {
  type Address,
  type ContactInputRaw,
  type ContactList,
  contactToInput,
  emptyContactInput,
  formatPhone,
  normalizePhone,
} from "@kontaktfix/shared";
import {
  Avatar,
  Button,
  Checkbox,
  cn,
  Dialog,
  Field,
  Input,
  Sheet,
  Skeleton,
  Tabs,
  Textarea,
} from "@kontaktfix/ui";
import { CameraIcon, InfoIcon, PlusIcon, TrashIcon, WarningIcon } from "@phosphor-icons/react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { EntityHistory } from "@/components/history";
import { LIST_COLOR_HEX } from "@/components/list-badge";
import { api } from "@/lib/api";
import { fieldErrors, toastError } from "@/lib/errors";
import { useCanChangeContacts, useContact, useInvalidateData, useLists, useMe } from "@/lib/queries";

type AddressKey = "businessAddress" | "homeAddress" | "otherAddress";
const EMPTY_ADDRESS: Address = { street: "", postalCode: "", city: "", state: "", country: "" };

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-b border-border py-5 first:pt-0 last:border-b-0">
      <h3 className="mb-3 text-[12px] font-semibold tracking-wide text-fg-subtle uppercase">{title}</h3>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">{children}</div>
    </section>
  );
}

function PhotoPicker({
  contactId,
  name,
  hasPhoto,
  version,
  disabled,
  onChanged,
}: {
  contactId: string | null;
  name: string;
  hasPhoto: boolean;
  version: string | null;
  disabled: boolean;
  onChanged: () => void;
}) {
  const { t } = useTranslation();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const src = contactId && hasPhoto ? `/api/contacts/${contactId}/photo?v=${version}` : null;

  const upload = async (file: File) => {
    if (!contactId) return;
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.put(`/api/contacts/${contactId}/photo`, form);
      toast.success(t("contacts.photoSaved"));
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    if (!contactId) return;
    setBusy(true);
    try {
      await api.delete(`/api/contacts/${contactId}/photo`);
      onChanged();
    } catch (err) {
      toastError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-4">
      <div className="relative">
        <Avatar name={name || "?"} src={src} size={56} />
        {!disabled && contactId && (
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="absolute -right-1 -bottom-1 flex size-6 items-center justify-center rounded-full border border-border bg-bg text-fg-muted shadow-sm hover:text-fg"
            aria-label={t("contacts.uploadPhoto")}
          >
            <CameraIcon className="size-3.5" />
          </button>
        )}
      </div>
      <div className="min-w-0">
        <div className="truncate text-[15px] font-semibold">{name || " "}</div>
        {!disabled && contactId && (
          <div className="mt-1 flex gap-3 text-[12.5px]">
            <button
              type="button"
              className="text-accent hover:underline"
              disabled={busy}
              onClick={() => input.current?.click()}
            >
              {t("contacts.uploadPhoto")}
            </button>
            {hasPhoto && (
              <button
                type="button"
                className="text-fg-muted hover:text-danger"
                disabled={busy}
                onClick={remove}
              >
                {t("contacts.removePhoto")}
              </button>
            )}
          </div>
        )}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) void upload(file);
          e.target.value = "";
        }}
      />
    </div>
  );
}

export function ContactSheet({
  contactId,
  defaultListIds = [],
  onClose,
}: {
  /** "new" for a new contact, an id to edit, null when closed. */
  contactId: string | null;
  defaultListIds?: string[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const isNew = contactId === "new";
  const { data: me } = useMe();
  const { data: contact, isLoading, refetch } = useContact(isNew ? null : contactId);
  const { data: lists = [] } = useLists();
  const invalidate = useInvalidateData();
  const [tab, setTab] = useState("details");
  const [form, setForm] = useState<ContactInputRaw>(emptyContactInput());
  const [listIds, setListIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [extraEmails, setExtraEmails] = useState(1);
  const [extraPhones, setExtraPhones] = useState(2);
  const [addresses, setAddresses] = useState<AddressKey[]>(["businessAddress"]);

  // Reset the form only when a different contact is opened.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentionally keyed on contactId
  useEffect(() => {
    setTab("details");
    setErrors({});
    if (isNew) {
      setForm(emptyContactInput());
      setListIds(defaultListIds);
      setExtraEmails(1);
      setExtraPhones(2);
      setAddresses(["businessAddress"]);
    }
  }, [contactId]);

  useEffect(() => {
    if (!contact || isNew) return;
    setForm(contactToInput(contact));
    setListIds(contact.listIds);
    setExtraEmails(contact.email3 ? 3 : contact.email2 ? 2 : 1);
    setExtraPhones(
      Math.max(
        2,
        [
          contact.businessPhone1,
          contact.mobilePhone,
          contact.businessPhone2,
          contact.homePhone1,
          contact.homePhone2,
        ].filter(Boolean).length,
      ),
    );
    const present = (["businessAddress", "homeAddress", "otherAddress"] as AddressKey[]).filter(
      (k) => contact[k],
    );
    setAddresses(present.length ? present : ["businessAddress"]);
  }, [contact, isNew]);

  const editableLists = useMemo(() => lists.filter((l) => l.canEdit), [lists]);
  const canChange = useCanChangeContacts();
  const canEdit = canChange && (isNew || Boolean(contact?.canEdit));
  const isAdmin = me?.role === "admin";
  const otherLists = useMemo(
    () => lists.filter((l) => listIds.includes(l.id) && !l.canEdit),
    [lists, listIds],
  );
  const name =
    [form.givenName, form.middleName, form.surname].filter(Boolean).join(" ") || form.companyName || "";

  const set =
    (key: keyof ContactInputRaw) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setForm((f) => ({ ...f, [key]: e.target.value }));
  const setAddress = (key: AddressKey, field: keyof Address) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [key]: { ...EMPTY_ADDRESS, ...(f[key] ?? {}), [field]: e.target.value } }));
  const formatPhoneOnBlur = (key: keyof ContactInputRaw) => () => {
    const value = form[key];
    if (typeof value === "string" && value.trim()) {
      const normalized = normalizePhone(value);
      setErrors((e) => {
        const next = { ...e };
        if (normalized) delete next[key];
        else next[key] = t("contacts.validation.invalid_phone");
        return next;
      });
      if (normalized) setForm((f) => ({ ...f, [key]: formatPhone(normalized) }));
    }
  };
  const err = (key: string) => errors[key] ?? null;

  const save = async () => {
    setSaving(true);
    setErrors({});
    try {
      if (isNew) {
        await api.post("/api/contacts", { contact: form, listIds });
        toast.success(t("contacts.created"));
        await invalidate();
        onClose();
      } else if (contactId) {
        await api.patch(`/api/contacts/${contactId}`, form);
        // List membership changes for lists the user can edit.
        const before = new Set(contact?.listIds ?? []);
        const after = new Set(listIds);
        for (const list of editableLists) {
          if (after.has(list.id) && !before.has(list.id)) {
            await api.post("/api/contacts/bulk/add-list", { contactIds: [contactId], listId: list.id });
          }
          if (!after.has(list.id) && before.has(list.id)) {
            await api.post("/api/contacts/bulk/remove-list", { contactIds: [contactId], listId: list.id });
          }
        }
        toast.success(t("contacts.updated"));
        await invalidate();
        onClose();
      }
    } catch (e) {
      const fields = fieldErrors(e);
      setErrors(fields);
      if (!Object.keys(fields).length) toastError(e);
      else toast.error(t("errors.validation_failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!contactId) return;
    try {
      await api.delete(`/api/contacts/${contactId}`);
      toast.success(t("contacts.deleted"));
      setConfirmDelete(false);
      await invalidate();
      onClose();
    } catch (e) {
      toastError(e);
    }
  };

  const toggleList = (list: ContactList) =>
    setListIds((ids) => (ids.includes(list.id) ? ids.filter((x) => x !== list.id) : [...ids, list.id]));

  const phoneSlots: (keyof ContactInputRaw)[] = [
    "mobilePhone",
    "businessPhone1",
    "businessPhone2",
    "homePhone1",
    "homePhone2",
  ];
  const emailSlots: (keyof ContactInputRaw)[] = ["email1", "email2", "email3"];
  const ro = !canEdit;

  return (
    <>
      <Sheet
        open={contactId !== null}
        onOpenChange={(open) => !open && onClose()}
        title={isNew ? t("contacts.new") : (contact?.displayName ?? t("common.loading"))}
        description={
          !canChange
            ? t("billing.readOnly")
            : !isNew && contact && !canEdit
              ? t("contacts.readOnlyHint")
              : undefined
        }
        footer={
          canEdit ? (
            <>
              {!isNew && isAdmin && (
                <Button variant="danger-ghost" className="mr-auto" onClick={() => setConfirmDelete(true)}>
                  <TrashIcon />
                  {t("common.delete")}
                </Button>
              )}
              <Button onClick={onClose}>{t("common.cancel")}</Button>
              <Button variant="primary" onClick={save} loading={saving}>
                {t("common.save")}
              </Button>
            </>
          ) : (
            <Button onClick={onClose}>{t("common.close")}</Button>
          )
        }
      >
        {!isNew && isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-14 w-60" />
            <Skeleton className="h-40" />
          </div>
        ) : (
          <>
            {!isNew && (
              <Tabs
                className="-mt-2 mb-5"
                value={tab}
                onValueChange={setTab}
                tabs={[
                  { value: "details", label: t("contacts.tabs.details") },
                  { value: "history", label: t("contacts.tabs.history") },
                ]}
              />
            )}
            {tab === "history" && contactId && !isNew ? (
              <EntityHistory entityType="contact" entityId={contactId} />
            ) : (
              <fieldset disabled={ro} className="min-w-0">
                <div className="mb-5">
                  <PhotoPicker
                    contactId={isNew ? null : contactId}
                    name={name}
                    hasPhoto={Boolean(contact?.hasPhoto)}
                    version={contact?.photoVersion ?? null}
                    disabled={ro}
                    onChanged={() => {
                      void refetch();
                      void invalidate();
                    }}
                  />
                </div>
                {otherLists.length > 0 && canEdit && !isNew && (
                  <div className="mb-5 flex items-start gap-2 rounded-md bg-warning-soft px-3 py-2 text-[12.5px] text-warning">
                    <WarningIcon className="mt-0.5 size-4 shrink-0" />
                    {t("contacts.sharedWarning", { lists: otherLists.map((l) => l.name).join(", ") })}
                  </div>
                )}
                {err("surname") === t("contacts.validation.name_or_company_required") && (
                  <div className="mb-4 rounded-md bg-danger-soft px-3 py-2 text-[12.5px] text-danger">
                    {err("surname")}
                  </div>
                )}

                <Section title={t("contacts.sections.person")}>
                  <Field label={t("contacts.fields.givenName")} error={null}>
                    <Input value={form.givenName ?? ""} onChange={set("givenName")} autoFocus={isNew} />
                  </Field>
                  <Field label={t("contacts.fields.surname")}>
                    <Input value={form.surname ?? ""} onChange={set("surname")} />
                  </Field>
                  <Field label={t("contacts.fields.title")}>
                    <Input value={form.title ?? ""} onChange={set("title")} placeholder="Dr." />
                  </Field>
                  <Field label={t("contacts.fields.middleName")}>
                    <Input value={form.middleName ?? ""} onChange={set("middleName")} />
                  </Field>
                  <Field label={t("contacts.fields.nickName")}>
                    <Input value={form.nickName ?? ""} onChange={set("nickName")} />
                  </Field>
                </Section>

                <Section title={t("contacts.sections.company")}>
                  <Field label={t("contacts.fields.companyName")} className="sm:col-span-2">
                    <CompanyInput
                      value={form.companyName ?? ""}
                      onChange={(v) => setForm((f) => ({ ...f, companyName: v }))}
                    />
                  </Field>
                  <Field label={t("contacts.fields.jobTitle")}>
                    <Input value={form.jobTitle ?? ""} onChange={set("jobTitle")} />
                  </Field>
                  <Field label={t("contacts.fields.department")}>
                    <Input value={form.department ?? ""} onChange={set("department")} />
                  </Field>
                  <Field label={t("contacts.fields.officeLocation")}>
                    <Input value={form.officeLocation ?? ""} onChange={set("officeLocation")} />
                  </Field>
                  <Field label={t("contacts.fields.profession")}>
                    <Input value={form.profession ?? ""} onChange={set("profession")} />
                  </Field>
                  <Field label={t("contacts.fields.manager")}>
                    <Input value={form.manager ?? ""} onChange={set("manager")} />
                  </Field>
                  <Field label={t("contacts.fields.assistantName")}>
                    <Input value={form.assistantName ?? ""} onChange={set("assistantName")} />
                  </Field>
                </Section>

                <Section title={t("contacts.sections.phone")}>
                  {phoneSlots.slice(0, extraPhones).map((key) => (
                    <Field key={key} label={t(`contacts.fields.${key}`)} error={err(key)}>
                      <Input
                        type="tel"
                        value={(form[key] as string) ?? ""}
                        onChange={set(key)}
                        onBlur={formatPhoneOnBlur(key)}
                        placeholder="+41 44 123 45 67"
                        aria-invalid={Boolean(err(key))}
                      />
                    </Field>
                  ))}
                  {!ro && extraPhones < phoneSlots.length && (
                    <AddMore onClick={() => setExtraPhones((n) => n + 1)}>{t("contacts.morePhones")}</AddMore>
                  )}
                </Section>

                <Section title={t("contacts.sections.email")}>
                  {emailSlots.slice(0, extraEmails).map((key) => (
                    <Field
                      key={key}
                      label={t(`contacts.fields.${key}`)}
                      error={err(key)}
                      className="sm:col-span-2"
                    >
                      <Input
                        type="email"
                        value={(form[key] as string) ?? ""}
                        onChange={set(key)}
                        placeholder="name@firma.ch"
                        aria-invalid={Boolean(err(key))}
                      />
                    </Field>
                  ))}
                  {!ro && extraEmails < 3 && (
                    <AddMore onClick={() => setExtraEmails((n) => n + 1)}>{t("contacts.moreEmails")}</AddMore>
                  )}
                  <Field label={t("contacts.fields.imAddress")} className="sm:col-span-2">
                    <Input
                      value={form.imAddress ?? ""}
                      onChange={set("imAddress")}
                      placeholder="sip:name@firma.ch"
                    />
                  </Field>
                </Section>

                <section className="border-b border-border py-5">
                  <h3 className="mb-3 text-[12px] font-semibold tracking-wide text-fg-subtle uppercase">
                    {t("contacts.sections.address")}
                  </h3>
                  <div className="flex flex-col gap-5">
                    {addresses.map((key) => (
                      <div key={key}>
                        <div className="mb-2 text-[12.5px] font-medium">
                          {t(`contacts.addressTypes.${key}`)}
                        </div>
                        <div className="grid grid-cols-6 gap-3">
                          <Input
                            className="col-span-6"
                            placeholder={t("contacts.fields.street")}
                            value={form[key]?.street ?? ""}
                            onChange={setAddress(key, "street")}
                          />
                          <Input
                            className="col-span-2"
                            placeholder={t("contacts.fields.postalCode")}
                            value={form[key]?.postalCode ?? ""}
                            onChange={setAddress(key, "postalCode")}
                          />
                          <Input
                            className="col-span-4"
                            placeholder={t("contacts.fields.city")}
                            value={form[key]?.city ?? ""}
                            onChange={setAddress(key, "city")}
                          />
                          <Input
                            className="col-span-3"
                            placeholder={t("contacts.fields.state")}
                            value={form[key]?.state ?? ""}
                            onChange={setAddress(key, "state")}
                          />
                          <Input
                            className="col-span-3"
                            placeholder={t("contacts.fields.country")}
                            value={form[key]?.country ?? ""}
                            onChange={setAddress(key, "country")}
                          />
                        </div>
                      </div>
                    ))}
                    {!ro && addresses.length < 3 && (
                      <div className="flex gap-2">
                        {(["businessAddress", "homeAddress", "otherAddress"] as AddressKey[])
                          .filter((k) => !addresses.includes(k))
                          .map((k) => (
                            <AddMore key={k} onClick={() => setAddresses((a) => [...a, k])}>
                              {t(`contacts.addressTypes.${k}`)}
                            </AddMore>
                          ))}
                      </div>
                    )}
                  </div>
                </section>

                <Section title={t("contacts.sections.more")}>
                  <Field
                    label={t("contacts.fields.website")}
                    error={err("website")}
                    className="sm:col-span-2"
                  >
                    <Input
                      value={form.website ?? ""}
                      onChange={set("website")}
                      placeholder="www.firma.ch"
                      aria-invalid={Boolean(err("website"))}
                    />
                  </Field>
                  <Field label={t("contacts.fields.notes")} className="sm:col-span-2">
                    <Textarea value={form.notes ?? ""} onChange={set("notes")} rows={3} />
                  </Field>
                </Section>
              </fieldset>
            )}
            {tab === "details" && (
              <section className="pt-5">
                <h3 className="mb-1 text-[12px] font-semibold tracking-wide text-fg-subtle uppercase">
                  {t("contacts.sections.lists")}
                </h3>
                <p className="mb-3 text-[12.5px] text-fg-muted">{t("contacts.listsHint")}</p>
                <div className="flex flex-col gap-1">
                  {lists.map((list) => {
                    const checked = listIds.includes(list.id);
                    const editable = list.canEdit && canEdit;
                    if (!editable && !checked) return null;
                    return (
                      <label
                        key={list.id}
                        className={cn(
                          "flex items-center gap-3 rounded-md border border-border px-3 py-2",
                          editable ? "cursor-pointer hover:bg-subtle" : "opacity-70",
                        )}
                      >
                        <Checkbox
                          checked={checked}
                          onCheckedChange={() => editable && toggleList(list)}
                          label={list.name}
                        />
                        <span
                          className="size-2 rounded-full"
                          style={{ background: LIST_COLOR_HEX[list.color] }}
                        />
                        <span className="flex-1 text-[13px]">{list.name}</span>
                        <span className="text-[12px] text-fg-subtle">
                          {t("lists.recipientsCount", { count: list.recipientCount })}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {isNew && !editableLists.some((l) => l.canEdit) && me?.role === "editor" && (
                  <p className="mt-2 text-[12.5px] text-danger">{t("contacts.listRequired")}</p>
                )}
                <p className="mt-4 flex items-center gap-1.5 text-[12px] text-fg-subtle">
                  <InfoIcon className="size-3.5" />
                  {t("contacts.managedNote")}
                </p>
              </section>
            )}
          </>
        )}
      </Sheet>
      <Dialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t("contacts.deleteTitle", { count: 1, n: 1 })}
        description={t("contacts.deleteText")}
        footer={
          <>
            <Button onClick={() => setConfirmDelete(false)}>{t("common.cancel")}</Button>
            <Button variant="danger" onClick={remove}>
              {t("common.delete")}
            </Button>
          </>
        }
      />
    </>
  );
}

function AddMore({ children, onClick }: { children: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-8 items-center gap-1.5 self-end rounded-md px-2 text-[12.5px] font-medium text-fg-muted hover:bg-muted hover:text-fg"
    >
      <PlusIcon className="size-3.5" />
      {children}
    </button>
  );
}

function CompanyInput({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [options, setOptions] = useState<string[]>([]);
  const id = "company-options";
  useEffect(() => {
    const handle = setTimeout(() => {
      if (value.length < 2) return setOptions([]);
      api
        .get<string[]>(`/api/contacts/companies?q=${encodeURIComponent(value)}`)
        .then(setOptions)
        .catch(() => setOptions([]));
    }, 200);
    return () => clearTimeout(handle);
  }, [value]);
  return (
    <>
      <Input value={value} onChange={(e) => onChange(e.target.value)} list={id} autoComplete="off" />
      <datalist id={id}>
        {options
          .filter((o) => o !== value)
          .map((o) => (
            <option key={o} value={o} />
          ))}
      </datalist>
    </>
  );
}
